import { PATHS } from "router/config/paths";
import { getAttributionParams } from "utils/firstTouchAttribution";
import { collapseDoubledCdnPrefix, GCS_CDN_BASE_URL } from "utils/cdnUrl";
import beepSound from "../sound/beep.mp3";
import { httpStatusOf } from "utils/httpStatusOf";

interface ImageCheckResult {
  isValid: boolean;
  width?: number;
  height?: number;
}

export const clipboardCopy = (text: string) => {
  try {
    navigator.clipboard.writeText(text);
  } catch {
    window.navigator["clipboard"].writeText(text);
  }
};

export const downloadImages = (imageURLs: string[]) => {
  imageURLs.forEach((imageURL, index) => {
    const link = document.createElement("a");
    link.href = imageURL;
    link.download = `image${index}.png`;
    link.click();
  });
};

export function isObjectEqual(obj1: any, obj2: any): boolean {
  // If the types are different, they are not equal
  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  // If they are primitive values, compare them using strict equality
  if (typeof obj1 !== "object" || obj1 === null || obj2 === null) {
    return obj1 === obj2;
  }

  // Compare the properties of the objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!isObjectEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export const isObjectEmpty = (object: any) => {
  if (object === null || object === undefined) return true;

  return Object.keys(object).length === 0;
};

export { formatDate, DateFormat } from "i18n/formatters/date";

// Humanize a plan code like "DESIGN_STUDIO" → "Design Studio". Shared by the Plan & Billing and
// Manage Members tabs so the two always render the same plan name. Returns "" for an empty code so
// callers can apply their own fallback.
export const humanizePackage = (s: string): string =>
  s
    ? s
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

export function playSound(sound = beepSound) {
  const audio = new Audio(sound);
  audio.play().catch((error) => {
    console.log("[Audio] Play failed - user interaction required", { status: httpStatusOf(error) });
  });
}

import { trackEvent } from "utils/analytics";

/** @deprecated Use trackEvent from "utils/analytics" instead */
export function AnalyticsEvent(event_name: string, category: string, action: string, params?: any) {
  trackEvent(event_name, { category, action, ...params });
}

export function getClearPath(path: PATHS) {
  // Split the path at the colon and take the first part
  let clearPath = path.split(":")[0];
  // Remove any trailing slash if present
  if (clearPath.endsWith("/")) {
    clearPath = clearPath.slice(0, -1);
  }

  return clearPath;
}

export function removeBackslash(path: string) {
  return path.replace(/\/$/, "");
}

// Merges persisted first-touch attribution (allowlisted, 30-day TTL) with the
// current URL search. Current URL wins on conflict so a later campaign URL
// always takes precedence over an older first-touch.
// See `src/utils/firstTouchAttribution.ts`.
export const getSearchParams = (): Record<string, string> => getAttributionParams();

export const checkImage = (url?: string): Promise<ImageCheckResult> => {
  if (!url) return Promise.resolve({ isValid: false });

  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        isValid: true,
        width: img.width,
        height: img.height,
      });
    };

    img.onerror = () => {
      resolve({ isValid: false });
    };

    img.src = url;
  });
};

export const checkMultipleImages = async (urls: string[]): Promise<{ [key: string]: ImageCheckResult }> => {
  const results = await Promise.all(
    urls.map(async (url) => ({
      url,
      result: await checkImage(url),
    }))
  );

  return results.reduce(
    (acc, { url, result }) => ({
      ...acc,
      [url]: result,
    }),
    {}
  );
};

export const formatSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export async function detectColorModelFast(file: File, sampleCount = 500): Promise<"rgb" | "rgba"> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Could not get 2D context");

      ctx.drawImage(img, 0, 0);
      const { data, width, height } = ctx.getImageData(0, 0, img.width, img.height);

      const totalPixels = width * height;

      // Limit sample count to total pixels
      const samples = Math.min(sampleCount, totalPixels);

      for (let s = 0; s < samples; s++) {
        const pixelIndex = Math.floor(Math.random() * totalPixels);
        const alphaIndex = pixelIndex * 4 + 3;

        if (data[alphaIndex] < 255) {
          resolve("rgba");

          return;
        }
      }

      resolve("rgb");
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export const normalizeImageUrl = (url?: string): string => {
  if (!url || typeof url !== "string") return "";
  const trimmed = collapseDoubledCdnPrefix(url);
  if (trimmed.length === 0) return "";

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    if (trimmed === `${GCS_CDN_BASE_URL}` || trimmed === GCS_CDN_BASE_URL.slice(0, -1)) {
      return "";
    }

    return trimmed;
  }

  if (trimmed.startsWith("/") || !trimmed.includes("://")) {
    const path = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
    const constructedUrl = `${GCS_CDN_BASE_URL}${path}`;
    if (constructedUrl === GCS_CDN_BASE_URL || constructedUrl === GCS_CDN_BASE_URL.slice(0, -1)) {
      return "";
    }

    return constructedUrl;
  }

  return "";
};

// Extension test on the PATH only, exactly like `isVideoUrl` in utils/media — a signed GCS url
// carries its signature AFTER the extension, and the old `includes(".zip")` form was blind in the
// other direction: it returned true for `?name=foo.zip.png`, i.e. an IMAGE misread as an archive.
// Same answer as before for every real archive url (signed or not); correct answer for that case.
// `[VIDEO_URL_DETECTION]` is the same mistake in the video half of this problem.
export const isZipUrl = (url?: string | null): boolean => {
  if (typeof url !== "string" || url.length === 0) return false;

  return url.split("?")[0].split("#")[0].toLowerCase().endsWith(".zip");
};

export async function detectColorModelFromUrl(url: string, sampleCount = 500): Promise<"rgb" | "rgba"> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Could not get 2D context");

      ctx.drawImage(img, 0, 0);
      const { data, width, height } = ctx.getImageData(0, 0, img.width, img.height);

      const totalPixels = width * height;

      // Limit sample count to total pixels
      const samples = Math.min(sampleCount, totalPixels);

      for (let s = 0; s < samples; s++) {
        const pixelIndex = Math.floor(Math.random() * totalPixels);
        const alphaIndex = pixelIndex * 4 + 3;

        if (data[alphaIndex] < 255) {
          resolve("rgba");

          return;
        }
      }

      resolve("rgb");
    };

    img.onerror = reject;
    img.src = url;
  });
}
